import { RANKS, type RankId } from '@kolony/shared';

/**
 * What the rail, the badge and the promotion all read. A VIEW MODEL, so it lives in `ui/`:
 * the §16.1 fence forbids ui from importing game-controller internals, and `RankService` is
 * the controller that PRODUCES one of these.
 */
export interface RankView {
  /** null ⇒ unranked: fewer than `placementGames` rated games. */
  rank: RankId | null;
  placed: boolean;
  ratedGames: number;
  placementGames: number;
  /** Withheld by the server until placed, so absent here too. */
  rating: number | null;
  everHeld: readonly RankId[];
  /** True for a guest — drives the "Save my rank" switch. */
  guest: boolean;
}

// Presentation for the rank ladder (H14). The engine and the wire speak `squire`; the
// English lives here, the same split `cardInfo.ts` draws for card ids (D154).
//
// The blurbs are what the promotion screen says under the name. They describe the SHIELD,
// not the player: the ladder's whole idea is that the material is the rank, so the line a
// player reads at their proudest moment should name the thing they just earned.

export const RANK_NAMES: Record<RankId, string> = {
  beggar: 'Beggar',
  peasant: 'Peasant',
  squire: 'Squire',
  knight: 'Knight',
  duke: 'Duke',
  king: 'King',
  emperor: 'Emperor',
};

export const RANK_BLURBS: Record<RankId, string> = {
  beggar: 'Rope-bound planks. Nothing to declare yet.',
  peasant: 'Sound oak and a plain iron rim.',
  squire: 'Brass, varnish, and your first painted mark.',
  knight: 'Steel and true heraldry.',
  duke: 'Gilt scrollwork over engraved silver.',
  king: 'Solid gold, crowned, engraved edge to edge.',
  emperor: 'Gem-set gold. The top of the ladder.',
};

/** The badge shown for a player with no rank yet: the ladder's silhouette, blacked out. */
export const UNRANKED_BADGE = 'unranked';
export const UNRANKED_LABEL = 'Unranked';

/**
 * The rail needs a span for the two open-ended bands to draw a bar at all. These are
 * DRAWING constants and deliberately not in `shared/config/ranks.ts`: beggar genuinely has
 * no floor and emperor genuinely has no ceiling, and writing one into the ladder would be
 * inventing a bound the rating does not have.
 */
export const RAIL_FLOOR = 900;
export const RAIL_CEILING = 1950;

/** `<rankId>` or `unranked`, 96px — the badge beside a name. */
export function rankBadgeUrl(rank: RankId | null): string {
  return `/assets/icons/ranks/${rank ?? UNRANKED_BADGE}.webp`;
}

/** The same art at 512px, for the promotion reveal. */
export function rankArtUrl(rank: RankId | null): string {
  return `/assets/icons/ranks/large/${rank ?? UNRANKED_BADGE}.webp`;
}

export function rankName(rank: RankId | null): string {
  return rank === null ? UNRANKED_LABEL : RANK_NAMES[rank];
}

/** The band a rank covers, as a player-facing string. */
export function rankBandLabel(rank: RankId): string {
  const i = RANKS.findIndex((b) => b.id === rank);
  const floor = RANKS[i]!.floor;
  const next = RANKS[i + 1];
  if (floor === null) return `below ${RANKS[1]!.floor}`;
  return next ? `${floor} – ${next.floor! - 1}` : `${floor} and above`;
}

export interface RailGeometry {
  /** 0..1 across the current band. */
  progress: number;
  /** The band being climbed toward, or null at the top of the ladder. */
  next: RankId | null;
  lo: number;
  hi: number;
}

/** Where a rating sits inside its band, and what is next. */
export function railGeometry(rating: number, rank: RankId): RailGeometry {
  const i = RANKS.findIndex((b) => b.id === rank);
  const lo = RANKS[i]!.floor ?? RAIL_FLOOR;
  const next = RANKS[i + 1] ?? null;
  const hi = next ? next.floor! : RAIL_CEILING;
  const progress = Math.max(0, Math.min(1, (rating - lo) / (hi - lo)));
  return { progress, next: next?.id ?? null, lo, hi };
}

/**
 * How blurred the next rank's shield is, in pixels, for a badge drawn at `sizePx`.
 *
 * Not a binary: it eases off as the bar fills, so the thing being climbed toward slowly
 * comes into focus. A rank once held never blurs again — you have seen it, and hiding it
 * would be pretending you had not.
 *
 * The radius is a FRACTION OF THE BADGE, not a constant. A blur tuned for a 90px shield
 * erases a 42px one completely, and a goal you cannot see at all is not a goal — it is a
 * smudge. Scaling keeps the same amount of tease at every size the rail is drawn.
 */
export function nextBlurPx(progress: number, everHeld: boolean, sizePx = 90): number {
  if (everHeld) return 0;
  const maxBlur = sizePx * 0.17;
  return Math.max(sizePx * 0.03, maxBlur * (1 - progress));
}
